import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { PhoneInput } from '@/components/ui/phone-input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import { useApi } from '@/hooks/useApi';
import { CarrierType, CreateCarrierDto, CARRIER_TYPE_LABELS } from '@/types/carrier.types';
import { Loader2 } from 'lucide-react';

interface AddCarrierModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export function AddCarrierModal({ open, onClose, onSuccess }: AddCarrierModalProps) {
  const { loading, post } = useApi({ showSuccessToast: true, showErrorToast: true });
  
  const [formData, setFormData] = useState<CreateCarrierDto>({
    name: '',
    type: CarrierType.PLATFORM_MANAGED,
    logo_url: '',
    contact_info: {
      email: '',
      phone: '',
      address: ''
    },
    is_guaranteed_payout: false,
    payout_duration_in_hours: 72,
    wallet_balance: 0,
    credit_limit: 0,
    is_active: true
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const response = await post('/api/v1/carriers', formData);
    
    if (response?.success) {
      onSuccess();
      onClose();
      resetForm();
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      type: CarrierType.PLATFORM_MANAGED,
      logo_url: '',
      contact_info: {
        email: '',
        phone: '',
        address: ''
      },
      is_guaranteed_payout: false,
      payout_duration_in_hours: 72,
      wallet_balance: 0,
      credit_limit: 0,
      is_active: true
    });
  };

  const updateContactInfo = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      contact_info: {
        ...prev.contact_info,
        [field]: value
      }
    }));
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>إضافة ناقل جديد</DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="name">اسم الناقل *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="أدخل اسم الناقل"
                required
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="type">النوع *</Label>
              <Select
                value={formData.type}
                onValueChange={(value) => setFormData(prev => ({ ...prev, type: value as CarrierType }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="اختر النوع" />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(CARRIER_TYPE_LABELS).map(([value, label]) => (
                    <SelectItem key={value} value={value}>{label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="logo_url">رابط الشعار</Label>
            <Input
              id="logo_url"
              type="url"
              value={formData.logo_url || ''}
              onChange={(e) => setFormData(prev => ({ ...prev, logo_url: e.target.value }))}
              placeholder="https://example.com/logo.png"
            />
          </div>

          {/* Contact Info */}
          <div className="space-y-4">
            <h3 className="font-semibold text-sm">معلومات التواصل</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="contact_email">البريد الإلكتروني</Label>
                <Input
                  id="contact_email"
                  type="email"
                  value={formData.contact_info?.email || ''}
                  onChange={(e) => updateContactInfo('email', e.target.value)}
                  placeholder="email@example.com"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="contact_phone">رقم الهاتف</Label>
                <PhoneInput
                  value={formData.contact_info?.phone || ''}
                  onChange={(val) => updateContactInfo('phone', val)}
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="contact_address">العنوان</Label>
              <Textarea
                id="contact_address"
                value={formData.contact_info?.address || ''}
                onChange={(e) => updateContactInfo('address', e.target.value)}
                placeholder="أدخل العنوان"
                rows={2}
              />
            </div>
          </div>

          {/* Financial Settings */}
          <div className="space-y-4">
            <h3 className="font-semibold text-sm">الإعدادات المالية</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="wallet_balance">رصيد المحفظة</Label>
                <Input
                  id="wallet_balance"
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.wallet_balance}
                  onChange={(e) => setFormData(prev => ({ ...prev, wallet_balance: parseFloat(e.target.value) || 0 }))}
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="credit_limit">حد الائتمان</Label>
                <Input
                  id="credit_limit"
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.credit_limit}
                  onChange={(e) => setFormData(prev => ({ ...prev, credit_limit: parseFloat(e.target.value) || 0 }))}
                />
              </div>
            </div>

            <div className="flex items-center gap-2">
              <Checkbox
                id="is_guaranteed_payout"
                checked={formData.is_guaranteed_payout}
                onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_guaranteed_payout: !!checked }))}
              />
              <Label htmlFor="is_guaranteed_payout" className="cursor-pointer">
                دفع مضمون
              </Label>
            </div>

            {formData.is_guaranteed_payout && (
              <div className="space-y-2">
                <Label htmlFor="payout_duration">مدة الدفع (بالساعات)</Label>
                <Input
                  id="payout_duration"
                  type="number"
                  min="1"
                  max="168"
                  value={formData.payout_duration_in_hours}
                  onChange={(e) => setFormData(prev => ({ ...prev, payout_duration_in_hours: parseInt(e.target.value) || 72 }))}
                />
              </div>
            )}
          </div>

          {/* Status */}
          <div className="flex items-center gap-2">
            <Checkbox
              id="is_active"
              checked={formData.is_active}
              onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_active: !!checked }))}
            />
            <Label htmlFor="is_active" className="cursor-pointer">
              نشط
            </Label>
          </div>

          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={onClose}>
              إلغاء
            </Button>
            <Button type="submit" disabled={loading || !formData.name}>
              {loading && <Loader2 className="h-4 w-4 ms-2 animate-spin" />}
              إضافة الناقل
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
